'use client'

import { IPageDataValue, useSession, useUpFetch } from '@repo/sdk'
import { IPageData } from '@repo/sdk'
import { useMutation } from '@tanstack/react-query'
import {
  ColumnDef,
  ColumnFilter,
  getCoreRowModel,
  type SortingState,
  type Table,
  useReactTable,
} from '@tanstack/react-table'
import * as qs from 'neoqs'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'

interface ITableStateContext<TData = unknown, TValue = unknown> {
  columns: ColumnDef<TData, TValue>[]
  columnVisibility: Record<string, boolean>
  setColumnVisibility: Dispatch<SetStateAction<Record<string, boolean>>>
  setData: Dispatch<SetStateAction<TData[]>>
  setPageCount: Dispatch<SetStateAction<number>>
  setSorting: Dispatch<SetStateAction<SortingState>>
  sorting: SortingState
  table: Table<TData>
}

export const TableStateContext = createContext<ITableStateContext>({} as ITableStateContext)

export const TableStateProvider = <TData = unknown, TValue = unknown>({
  children,
  columns,
  initialColumnVisibility = {},
}: {
  children: ReactNode
  columns: ColumnDef<TData, TValue>[]
  initialColumnVisibility?: Record<string, boolean>
}) => {
  'use memo'

  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility)
  const [sorting, setSorting] = useState<SortingState>([])
  const [pageCount, setPageCount] = useState(0)
  const [data, setData] = useState<TData[]>([])
  const [filters, setFilters] = useState<ColumnFilter[]>([])
  const pathname = usePathname()

  // Parse search params from the URL
  const searchParams = useSearchParams()
  const parsedParams = qs.parse(searchParams.toString()) as Record<
    string,
    Record<string, any> | string
  >

  // Update filters based on parsed search params
  useEffect(() => {
    if (parsedParams.filter) {
      setFilters(
        Object.entries(parsedParams.filter).map(([key, value]) => ({
          id: key,
          value,
        })),
      )
    }
  }, [])

  // Update sorting based on parsed search params and data
  useEffect(() => {
    if (data.length && parsedParams.sort) {
      setSorting(
        Object.entries(parsedParams.sort).map(([key, values]) => ({
          desc: values! === 'desc',
          id: key,
        })),
      )
    }
  }, [data])

  const router = useRouter()

  // Update URL search params based on filters
  useEffect(() => {
    const params = new URLSearchParams(searchParams)

    filters.forEach(filter => {
      params.set(`filter[${filter.id}]`, `${filter.value}`)
    })

    //remove filters that are not in the table
    const filtersToRemove = parsedParams.filter
      ? Object.keys(parsedParams.filter).filter(key => !filters.some(filter => filter.id === key))
      : []

    filtersToRemove.forEach(filter => {
      params.delete(`filter[${filter}]`)
    })

    router.replace(`${pathname}?${params.toString()}`)
  }, [filters])

  // Create the table instance
  const table = useReactTable({
    columns,
    data,
    defaultColumn: {
      enableColumnFilter: false,
      enableHiding: true,
      enableSorting: false,
      size: 100,
    },
    enableMultiSort: false,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    pageCount,
    state: {
      columnFilters: filters,
      columnVisibility,
      sorting,
    },
  })

  // Fetch table settings from the server
  const upFetch = useUpFetch()
  const pathnameSlug =
    pathname === '/'
      ? 'dashboard'
      : pathname
          .replace(/^\/+/, '') // Replace the first slash with nothing
          .replace(/\//g, '-') // Replace remaining slashes with hyphens
  const session = useSession()

  useEffect(() => {
    if (!session?.data?.user) return
    if (!table) return

    upFetch<IPageData>(`/users/${session.data.user.id}/pages/${pathnameSlug}/data`).then(res => {
      setColumnVisibility(og => res.value?.table?.visibility ?? og)
    })
  }, [session?.data?.user])

  // Mutation to update table settings on the server
  const updateTableSettingsMutation = useMutation({
    mutationFn: async (data: IPageDataValue) => {
      await upFetch<{ data: IPageData }>(
        `/users/${session.data?.user?.id}/pages/${pathnameSlug}/data`,
        {
          body: data,
          method: 'POST',
        },
      )
    },
  })

  // Update table settings on the server when column visibility changes
  useEffect(() => {
    if (!session?.data?.user) return

    updateTableSettingsMutation.mutate({
      table: {
        visibility: columnVisibility,
      },
    })
  }, [columnVisibility])

  // Provide the table state context to children
  return (
    <TableStateContext.Provider
      value={{
        // @ts-expect-error
        columns,
        columnVisibility,
        setColumnVisibility,
        // @ts-expect-error
        setData,
        setPageCount,
        setSorting,
        sorting,
        // @ts-expect-error
        table,
      }}
    >
      {children}
    </TableStateContext.Provider>
  )
}

// Custom hook to access the table state context
export const useTableState = <T = unknown,>() => {
  const context = useContext(TableStateContext) as ITableStateContext<T>

  if (!context) {
    throw new Error('useTableState must be used within a TableStateProvider')
  }

  return context
}
